/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
/**
 * Sprint 8 Day-3 — /signals feed page.
 *
 * Single page; cards branch by `signal.source` to surface channel-specific
 * affordances (Reddit shows author trust + community rules; Quora shows
 * draft as a multi-paragraph answer; LinkedIn shows mutual-connection chip
 * and group meta). Common shell: header, "why Maya flagged it", footer
 * actions.
 *
 * Hard MVP rule: every action is "Copy & open in tab" — we never post to
 * the community channel on the user's behalf.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Sparkles, MessageCircle, Send, Linkedin, ExternalLink, RefreshCw, Copy, CheckCircle2, X, Inbox, Settings2, Users, Award, Twitter } from 'lucide-react';
import { toast } from "sonner";
import { signalsApi } from "@/lib/api";
import RmcProjectsTable from "@/pages/signals/RmcProjectsTable";
import { useAssistantName } from "@/hooks/useAssistantName";

const CHANNEL_META = {
  reddit:         { Icon: MessageCircle, label: "Reddit",
                    ring: "text-orange-700 bg-orange-50 ring-orange-200",
                    accent: "text-orange-600" },
  quora:          { Icon: Send,          label: "Quora",
                    ring: "text-red-700 bg-red-50 ring-red-200",
                    accent: "text-red-600" },
  linkedin_group: { Icon: Linkedin,      label: "LinkedIn Group",
                    ring: "text-blue-700 bg-blue-50 ring-blue-200",
                    accent: "text-blue-600" },
  x:              { Icon: Twitter,       label: "X",
                    ring: "text-slate-800 bg-slate-100 ring-slate-300",
                    accent: "text-slate-800" },
};

const INTENT_LABEL = {
  pricing_complaint: "Pricing pain",
  team_build:        "Team build",
  tool_switch:       "Tool switch",
  fundraising:       "Fundraising",
  scale_pain:        "Scale pain",
  other:             "General intent",
};

function fmtAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

async function _copy(text) {
  try {
    await navigator.clipboard.writeText(text || "");
    toast.success("Copied draft to clipboard");
  } catch {
    toast.error("Copy failed — select and copy manually");
  }
}

function RedditBody({ signal }) {
  const am = signal.author_meta || {};
  return (
    <>
      <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500">
        <span className="font-medium text-slate-700">{signal.author_handle}</span>
        {am.karma_in_sub !== undefined && (
          <span>· {am.karma_in_sub} karma in this sub</span>)}
        {am.account_age_days !== undefined && (
          <span>· {Math.round(am.account_age_days / 365)}y old</span>)}
      </div>
      <p className="mt-2 text-[13px] text-slate-700 leading-relaxed line-clamp-3">
        {signal.body_text}
      </p>
    </>
  );
}

function QuoraBody({ signal }) {
  const am = signal.author_meta || {};
  return (
    <>
      <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500">
        <span className="font-medium text-slate-700">{signal.author_name}</span>
        {am.is_top_writer && (
          <span className="inline-flex items-center gap-1 text-amber-700">
            <Award size={10} /> Top writer
          </span>
        )}
        {am.follower_count !== undefined && (
          <span>· {am.follower_count} followers</span>)}
      </div>
      <p className="mt-2 text-[13px] text-slate-700 leading-relaxed">
        {signal.body_text}
      </p>
    </>
  );
}

function LinkedInBody({ signal }) {
  const am = signal.author_meta || {};
  const cm = signal.community_meta || {};
  return (
    <>
      <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500 flex-wrap">
        <span className="font-medium text-slate-700">{signal.author_name}</span>
        {am.title && <span>· {am.title}</span>}
        {cm.member_count !== undefined && (
          <span className="inline-flex items-center gap-1">
            <Users size={10} /> {cm.member_count.toLocaleString()} members
          </span>
        )}
        {am.shared_connections > 0 && (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
            · {am.shared_connections} mutual connections
          </span>
        )}
      </div>
      <p className="mt-2 text-[13px] text-slate-700 leading-relaxed line-clamp-3">
        {signal.body_text}
      </p>
    </>
  );
}

function XBody({ signal }) {
  const am = signal.author_meta || {};
  return (
    <>
      <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500 flex-wrap">
        <span className="font-medium text-slate-700">{signal.author_handle}</span>
        {am.verified && (
          <span className="inline-flex items-center gap-1 text-blue-600">
            <CheckCircle2 size={10} /> verified
          </span>
        )}
        {am.follower_count !== undefined && (
          <span>· {am.follower_count.toLocaleString()} followers</span>)}
      </div>
      <p className="mt-2 text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap line-clamp-3">
        {signal.body_text}
      </p>
    </>
  );
}

const BODIES = { reddit: RedditBody, quora: QuoraBody,
                 linkedin_group: LinkedInBody, x: XBody };

function SignalCard({ signal, onActioned, onDismissed }) {
  const agentName = useAssistantName();
  const cm = CHANNEL_META[signal.source] || CHANNEL_META.reddit;
  const sa = signal.suggested_action || {};
  const primary = sa.primary || {};
  const Body = BODIES[signal.source] || RedditBody;
  const [busy, setBusy] = useState(false);

  async function act() {
    if (!primary.kind || primary.kind === "skip") {
      window.open(signal.external_url, "_blank", "noopener,noreferrer");
      return;
    }
    setBusy(true);
    try {
      await _copy(primary.draft || "");
      await signalsApi.action(signal.id, primary.kind, "user-clicked");
      window.open(signal.external_url, "_blank", "noopener,noreferrer");
      onActioned?.(signal.id);
    } catch (e) {
      toast.error(e.message || "Failed to mark actioned");
    } finally {
      setBusy(false);
    }
  }

  async function dismiss(reason) {
    setBusy(true);
    try {
      await signalsApi.dismiss(signal.id, reason);
      toast("Dismissed");
      onDismissed?.(signal.id);
    } catch (e) {
      toast.error(e.message || "Dismiss failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article data-testid={`signal-card-${signal.id}`}
      className="rounded-xl border border-slate-200 bg-white px-4 sm:px-5 py-4 transition-colors hover:border-indigo-200 min-w-0">

      {/* Header */}
      <div className="flex items-start gap-3 min-w-0">
        <span className={cn("inline-flex h-7 w-7 items-center justify-center rounded-lg ring-1 shrink-0", cm.ring)}>
          <cm.Icon size={13} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-500">
            <span className={cn("font-semibold", cm.accent)}>{cm.label}</span>
            <span className="truncate max-w-[40vw] sm:max-w-none">· {signal.community}</span>
            <span>· {fmtAgo(signal.posted_at)} ago</span>
            <span className="sm:ml-auto inline-flex items-center gap-1 text-slate-400">
              relevance <span className="font-semibold text-slate-700">{Math.round(signal.relevance_score * 100)}%</span>
            </span>
          </div>
          <h3 className="mt-1 text-[14px] font-semibold text-slate-900 leading-snug break-words">{signal.title}</h3>
          <Body signal={signal} />
        </div>
      </div>

      {/* Why Maya flagged it */}
      {(signal.intent_tag || sa.why) && (
        <div className="mt-3 pl-0 sm:pl-10 text-[11px] text-slate-500 flex flex-wrap items-center gap-1.5 min-w-0">
          <Sparkles size={10} className="text-indigo-500 shrink-0" />
          <span className="min-w-0 break-words">{sa.why || `Matched intent: ${INTENT_LABEL[signal.intent_tag] || signal.intent_tag}`}</span>
          {signal.intent_tag && (
            <Badge variant="outline" className="h-5 sm:h-4 text-[9.5px] font-medium">
              {INTENT_LABEL[signal.intent_tag] || signal.intent_tag}
            </Badge>
          )}
        </div>
      )}

      {/* Maya suggests */}
      {primary.draft && (
        <div className="mt-3 pl-0 sm:pl-10 min-w-0">
          <div className="rounded-lg bg-slate-50/80 ring-1 ring-slate-200 px-3 py-2.5 min-w-0">
            <div className="flex flex-wrap items-baseline gap-1.5 text-[10.5px] uppercase tracking-wider text-indigo-600 font-semibold">
              <span className="inline-flex items-center gap-1"><Sparkles size={9} /> {agentName} suggests · {primary.kind}</span>
              {primary.confidence !== undefined && (
                <span className="sm:ml-auto text-slate-500 normal-case tracking-normal font-normal">
                  confidence {Math.round(primary.confidence * 100)}%
                </span>
              )}
            </div>
            <p className="mt-1 text-[12.5px] text-slate-700 leading-relaxed whitespace-pre-wrap break-words">
              {primary.draft}
            </p>
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div className="mt-3 pl-0 sm:pl-10 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:gap-1.5 flex-wrap">
        <Button size="sm" className="h-10 sm:h-8 text-[11.5px] w-full sm:w-auto" onClick={act} disabled={busy}
          data-testid={`signal-act-${signal.id}`}>
          <Copy size={11} /> Copy &amp; open {cm.label}
          <ExternalLink size={10} className="opacity-60" />
        </Button>
        <Button size="sm" variant="ghost" className="h-10 sm:h-8 text-[11.5px] w-full sm:w-auto"
          onClick={() => dismiss("not_icp")} disabled={busy}
          data-testid={`signal-dismiss-${signal.id}`}>
          <X size={11} /> Not relevant
        </Button>
      </div>
    </article>
  );
}

export default function SignalsPage() {
  const nav = useNavigate();
  const agentName = useAssistantName();
  const [filter, setFilter] = useState({ source: "all", status: "new" });
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [rncRefreshToken, setRncRefreshToken] = useState(0);

  const load = useCallback(async () => {
    if (filter.status === "rnc") {
      setRncRefreshToken((n) => n + 1);
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const params = { status: filter.status, minScore: 0 };
      if (filter.source !== "all") params.source = filter.source;
      const data = await signalsApi.list(params);
      setState({ loading: false, data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e.message || "Failed to load" });
    }
  }, [filter.source, filter.status]);

  useEffect(() => { queueMicrotask(load); }, [load]);

  const removeFromList = (id) => setState((s) => ({
    ...s, data: s.data ? { ...s.data, signals: s.data.signals.filter((x) => x.id !== id), total: s.data.total - 1 } : null,
  }));

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full min-w-0 overflow-y-auto overflow-x-hidden scrollbar-thin bg-white" data-testid="signals-page">
      <div className="max-w-[960px] mx-auto w-full px-4 sm:px-8 py-6 sm:py-10 space-y-5 sm:space-y-6 min-w-0">

        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
          <div className="min-w-0">
            <h1 className="text-[24px] sm:text-[32px] font-semibold tracking-tight text-slate-900">Signals</h1>
            <p className="mt-1.5 text-[13px] sm:text-[13.5px] text-slate-500 max-w-[640px] leading-relaxed">
              {agentName} watches your subreddits, Quora topics, and LinkedIn groups for posts worth your time.
              You decide whether to engage — every action opens the source in a new tab with the draft on your clipboard.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
            <Button variant="outline" size="sm" className="h-10 sm:h-8 flex-1 sm:flex-none" onClick={() => nav("/signals/sources")} data-testid="signals-sources-link">
              <Settings2 size={12} /> Sources
            </Button>
            <Button variant="ghost" size="sm" className="h-10 sm:h-8 text-slate-500 flex-1 sm:flex-none" onClick={load} disabled={state.loading} data-testid="signals-refresh">
              <RefreshCw size={12} className={cn(state.loading && "animate-spin")} /> Refresh
            </Button>
          </div>
        </header>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap text-[12px]" data-testid="signals-filters">
          {[
            { id: "all",            label: "All sources" },
            { id: "reddit",         label: "Reddit" },
            { id: "quora",          label: "Quora" },
            { id: "linkedin_group", label: "LinkedIn" },
            { id: "x",              label: "X" },
          ].map((s) => (
            <button key={s.id}
              type="button"
              onClick={() => setFilter((f) => ({ ...f, source: s.id }))}
              className={cn("min-h-9 sm:h-7 px-3 rounded-full ring-1 transition-colors",
                filter.source === s.id ? "bg-indigo-50 text-indigo-700 ring-indigo-200"
                                       : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50")}
              data-testid={`signals-filter-source-${s.id}`}>
              {s.label}
            </button>
          ))}
          <span className="hidden sm:inline mx-2 text-slate-300">|</span>
          {[
            { id: "new",      label: "New" },
            { id: "actioned", label: "Actioned" },
            { id: "dismissed",label: "Dismissed" },
            { id: "rnc", label: "RNC" },
          ].map((s) => (
            <button key={s.id}
              type="button"
              onClick={() => setFilter((f) => ({ ...f, status: s.id }))}
              className={cn("min-h-9 sm:h-7 px-3 rounded-full ring-1 transition-colors",
                filter.status === s.id ? "bg-slate-900 text-white ring-slate-900"
                                       : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50")}
              data-testid={`signals-filter-status-${s.id}`}>
              {s.label}
            </button>
          ))}
        </div>

        {filter.status === "rnc" ? (
          <RmcProjectsTable refreshToken={rncRefreshToken} />
        ) : (
        <div className="space-y-3" data-testid="signals-list">
          {state.loading && Array.from({ length: 3 }, (_, idx) => ({ id: `skeleton-${idx}` })).map((item) => (
            <Skeleton key={item.id} className="h-[180px] rounded-xl" />
          ))}
          {!state.loading && state.error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[12.5px] text-rose-700 break-words">
              Couldn&apos;t load signals: {state.error}
            </div>
          )}
          {!state.loading && state.data && state.data.signals.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 sm:px-5 py-10 sm:py-12 text-center" data-testid="signals-empty">
              <Inbox size={22} className="mx-auto text-slate-400" />
              <div className="mt-2 text-[13.5px] font-semibold text-slate-900">
                {filter.status === "new" ? "Nothing to act on right now" : "No signals here"}
              </div>
              <p className="mt-1 text-[12px] text-slate-500 max-w-[380px] mx-auto leading-relaxed">
                {filter.status === "new"
                  ? `When ${agentName} finds posts that match your ICP, they'll appear here. Add a source to get started.`
                  : "Try a different filter."}
              </p>
              <Button size="sm" variant="outline" className="h-10 sm:h-8 text-[11.5px] mt-4 w-full sm:w-auto" onClick={() => nav("/signals/sources")}>
                <Settings2 size={11} /> Manage sources
              </Button>
            </div>
          )}
          {!state.loading && state.data?.signals.map((sig) => (
            <SignalCard key={sig.id} signal={sig}
              onActioned={removeFromList} onDismissed={removeFromList} />
          ))}
        </div>
        )}
      </div>
    </div>
  );
}

RedditBody.propTypes = {
  signal: PropTypes.shape({
    author_handle: PropTypes.string,
    author_meta: PropTypes.shape({
      karma_in_sub: PropTypes.number,
      account_age_days: PropTypes.number,
    }),
    body_text: PropTypes.string,
  }).isRequired,
};

QuoraBody.propTypes = {
  signal: PropTypes.shape({
    author_name: PropTypes.string,
    author_meta: PropTypes.shape({
      is_top_writer: PropTypes.bool,
      follower_count: PropTypes.number,
    }),
    body_text: PropTypes.string,
  }).isRequired,
};

LinkedInBody.propTypes = {
  signal: PropTypes.shape({
    author_name: PropTypes.string,
    author_meta: PropTypes.shape({
      title: PropTypes.string,
      shared_connections: PropTypes.number,
    }),
    community_meta: PropTypes.shape({
      member_count: PropTypes.number,
    }),
    body_text: PropTypes.string,
  }).isRequired,
};

XBody.propTypes = {
  signal: PropTypes.shape({
    author_handle: PropTypes.string,
    author_meta: PropTypes.shape({
      verified: PropTypes.bool,
      follower_count: PropTypes.number,
    }),
    body_text: PropTypes.string,
  }).isRequired,
};

SignalCard.propTypes = {
  signal: PropTypes.shape({
    id: PropTypes.string.isRequired,
    source: PropTypes.string.isRequired,
    community: PropTypes.string,
    posted_at: PropTypes.string,
    relevance_score: PropTypes.number,
    title: PropTypes.string,
    intent_tag: PropTypes.string,
    suggested_action: PropTypes.shape({
      why: PropTypes.string,
      primary: PropTypes.shape({
        kind: PropTypes.string,
        draft: PropTypes.string,
        confidence: PropTypes.number,
      }),
    }),
    external_url: PropTypes.string,
  }).isRequired,
  onActioned: PropTypes.func,
  onDismissed: PropTypes.func,
};
